import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mockDataUserSingleOption } from '../../../__mockData/mockTableData';
import {
  AdminPageHeader,
  Button,
  ButtonSize,
  ButtonStyles,
  ButtonTypes,
  Modal,
  TableWithSearchTemplate,
  Theme,
} from '../../../ui/components';
import { useAdminToast } from '../../../ui/components/AdminToast/AdminToast';
import OperationCenterErrorCard, {
  ErrorType,
} from '../../../ui/components/OperationCenterErrorCard/OperationCenterErrorCard';
import { useAPICall } from '../../../hooks';
import userService from '../../../sdk/services/userService';
import { LoggerService } from '../../../ui/utils';
import { DynamicObjectInterface } from '../../../sdk/type';
import getUserListConfig from './userListConfig';
import styles from './index.module.scss';

export const AdminUserList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const apiCall = useAPICall();
  const { adminToast } = useAdminToast();
  const [data, setData] = useState([]);
  const [selectedRowId, setSelectedRowId] = useState('');
  const [selectedRowName, setSelectedRowName] = useState('');
  const [showDelete, setShowDelete] = useState(false);
  const [displayEmpty, setDisplayEmpty] = useState(false);

  const fetchUserList = async () => {
    try {
      const result = await apiCall(userService.listUser);
      if (result.status === 200 && result?.data?.data?.length > 0) {
        setData(result?.data?.data);
      } else {
        if (result?.data?.data === null) {
          adminToast({
            message: t('admin.userEdit.noDataAvailable'),
            theme: Theme.SUCCESS,
          });
        }
        setDisplayEmpty(true);
      }
    } catch (error) {
      LoggerService.log('Failed to retrieve user list', error);
    }
  };

  useEffect(() => {
    fetchUserList();
  }, []);

  const onEdit = (id: string) => {
    navigate(`/admin/master/user-setup/edit?=${id}`);
  };

  const onClick = (id: string) => {
    //to show pop up modal
    setSelectedRowId(id);
    setShowDelete(true);
  };

  //const buttonConfig = [
  //  {
  //    title: t('admin.userSetup.addUser'),
  //     buttonStyle: ButtonStyles.SOLID,
  //     onClick: () => {
  //       navigate(`/admin/user-setup/edit?=new`);
  //     },
  //   },
  // ];
  const onDeleteAction = async () => {
    try {
      await apiCall(userService.deleteUser, `${selectedRowId}`);

      // Update the table after deletion
      fetchUserList();
      adminToast({
        message: t('admin.userEdit.deleteUserSuccess').replace('{0}', selectedRowName),
        theme: Theme.SUCCESS,
      });
      setShowDelete(false);
    } catch (error) {
      LoggerService.log('Error deleting user', error);
      adminToast({
        message: t('admin.userEdit.failDeleteUser'),
        theme: Theme.ERROR,
      });
    }
  };

  const deletePrompt = (
    <>
      {showDelete && (
        <Modal
          primaryButtonLabel={''}
          headline={`${t('admin.userSetup.deleteUser')} ${selectedRowId}?`}
          closeIcon={false}
          containerWidth={'460px'}
          primaryButton={
            <Button
              title={t('admin.userSetup.confirm')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                onDeleteAction();
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
            />
          }
          secondaryButton={
            <Button
              title={t('admin.userSetup.cancel')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                setShowDelete(false);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
              theme={Theme.BLACK}
              data-testid="test-cancel-button-popup-delete-modal"
            />
          }
        />
      )}
    </>
  );

  const onRowClick = (row: DynamicObjectInterface) => {
    setSelectedRowId(row?.userId);
    setSelectedRowName(row?.name);
  };

  return (
    <div className={styles['user-container']}>
      <AdminPageHeader title={t('admin.userSetup.title')} />
      {displayEmpty ? (
        <div className={styles['no-results-container']}>
          <OperationCenterErrorCard errorType={ErrorType.NO_RECORD} />
        </div>
      ) : (
        <TableWithSearchTemplate
          dataSource={data}
          handleReset={undefined}
          handleSearch={undefined}
          searchByStrip
          getColumns={getUserListConfig}
          data={mockDataUserSingleOption}
          rowKey="userId"
          selected={selectedRowId}
          cb={{ onEdit, onClick }}
          onRowClick={onRowClick}
        />
      )}

      {deletePrompt}
    </div>
  );
};
